import { type FC, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { determineDataSource } from "../../../../Data/DataStores/DataSource";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Request from "../../../../Utilities/Platform/Request";
import { type FileConnectionType } from "../MediaUtilities";

interface Props {
    connection: FileConnectionType;
}

interface ConnectionDetails {
    name: string;
    url: string | null;
}

async function loadCustomerDisplay(id: string): Promise<ConnectionDetails> {
    const source = await determineDataSource();

    const customerDisplay = await source.customerDisplays.get(id);

    return {
        name: customerDisplay.name,
        url : `/settings/customerdisplay/${customerDisplay.uuid}`,
    };
}

async function loadReceipt(id: string): Promise<ConnectionDetails> {
    const source = await determineDataSource();

    const receipt = await source.receipts.get(id);

    return {
        name: receipt.name,
        url : `/settings/receipt/${receipt.uuid}`,
    };
}

async function loadStatement(id: string): Promise<ConnectionDetails> {
    const response = await Request.post<{
        errors?: unknown;
        data: {
            statement: {
                id: string;
                name: string;
            };
        };
    }>("/graphql", {
        query: `query GetStatement($id: ID!) {
            statement(id: $id) {
                id,
                name,
            }
        }`,
        variables: {
            id,
        },
    });

    if(response.data.errors) {
        throw response.data.errors;
    }

    const statement = response.data.data.statement;

    return {
        name: statement.name,
        url : `/settings/statements/${statement.id}`,
    };
}

async function loadProduct(id: string): Promise<ConnectionDetails> {
    const source = await determineDataSource();

    const product = await source.products.get(id);

    return {
        name: product.name,
        url : `/product/${product.uuid}`,
    };
}

async function loadUser(id: string): Promise<ConnectionDetails> {
    const source = await determineDataSource();

    const user = await source.users.get(id);

    return {
        name: user.name,
        url : `/settings/users/${user.uuid}/edit`,
    };
}

async function loadOutlet(id: string): Promise<ConnectionDetails> {
    const source = await determineDataSource();

    const outlet = await source.outlets.get(id);

    return {
        name: outlet.name,
        url : null,
    };
}

async function loadSaleKeys(id: string): Promise<ConnectionDetails> {
    const source = await determineDataSource();

    const saleKeys = await source.salesKeys.get(id);

    return {
        name: saleKeys.name,
        url : `/settings/keys/${saleKeys.uuid}`,
    };
}

async function loadOrder(id: string): Promise<ConnectionDetails> {
    const response = await Request.post<{
        errors?: unknown;
        data: {
            order: {
                id: string;
                invoiceNumber: string;
            };
        };
    }>("/graphql", {
        query: `query GetOrder($id: ID!) {
            order(id: $id) {
                id,
                invoiceNumber,
            }
        }`,
        variables: {
            id,
        },
    });

    if(response.data.errors) {
        throw response.data.errors;
    }

    const order = response.data.data.order;

    return {
        name: `Order ${order.invoiceNumber}`,
        url : `/order/${order.id}`,
    };
}

async function loadOrderReview(id: string): Promise<ConnectionDetails> {
    const response = await Request.post<{
        errors?: unknown;
        data: {
            orderReview: {
                id: string;
                order: {
                    id: string;
                    invoiceNumber: string;
                };
            };
        };
    }>("/graphql", {
        query: `query GetOrder($id: ID!) {
            orderReview(id: $id) {
                id,
                order {
                    id,
                    invoiceNumber,
                }
            }
        }`,
        variables: {
            id,
        },
    });

    if(response.data.errors) {
        throw response.data.errors;
    }

    const orderReview = response.data.data.orderReview;

    return {
        name: `Order ${orderReview.order.invoiceNumber} Review`,
        url : `/order/${orderReview.order.id}/review/${orderReview.id}`,
    };
}

function loadDetails(connection: FileConnectionType): Promise<ConnectionDetails> {
    switch(connection.type) {
        case "App\\Models\\CustomerDisplay":
            return loadCustomerDisplay(connection.id);
        case "App\\Models\\Receipt":
            return loadReceipt(connection.id);
        case "App\\Models\\Statement":
            return loadStatement(connection.id);
        case "App\\Models\\Product":
            return loadProduct(connection.id);
        case "App\\Models\\User":
            return loadUser(connection.id);
        case "App\\Models\\SaleKey":
            return loadSaleKeys(connection.id);
        case "App\\Models\\Order":
            return loadOrder(connection.id);
        case "App\\Models\\Outlet":
            return loadOutlet(connection.id);
        case "OrderReview":
            return loadOrderReview(connection.id);
    }

    return Promise.resolve({
        name: "Unknown",
        url : null,
    });
}

export const FileConnection: FC<Props> = ({ connection }) => {
    const [ details, setDetails ] = useState<ConnectionDetails | null>(null);

    useEffect(() => {
        setDetails(null);

        loadDetails(connection)
            .then(details => {
                setDetails(details);
            })
            .catch(err => {
                console.error("Error loading connection details", err);
                setDetails({
                    name: "Error",
                    url : null,
                });
            });
    }, [ connection ]);

    if(!details) {
        return (
            <div className="">
                <span>
                    <FontAwesomeLight icon="spinner-third" spin />
                </span>
                Loading...
            </div>
        );
    }

    const { url, name } = details;

    if(url === null) {
        return (
            <div>
                {name}
            </div>
        );
    }

    return (
        <Link to={url}>
            {name}
        </Link>
    );
};
